/**
 * <look-bundle-popup>
 *
 * Shared drawer that displays the product list for whichever look card a
 * shopper clicks the "+" on. The product cards are NOT embedded in the page:
 * on open, the drawer fetches them on demand from the look's own URL via the
 * Section Rendering API:
 *
 *   fetch('<look_url>?section_id=look-bundle-content')
 *
 * (see sections/look-bundle-content.liquid). This keeps ~N product cards per
 * look out of the homepage HTML until a shopper actually opens a drawer.
 *
 * - Listens at document level for clicks on [data-look-bundle-trigger].
 * - Opens via <dialog>.showModal() so we get free focus trap + ESC-to-close.
 * - Shows a spinner while fetching; injects the returned cards on success.
 * - Caches the fetched HTML by look handle so reopening the same look is instant.
 *
 * Markup contract (see snippets/look-bundle-popup.liquid):
 *   <look-bundle-popup hidden>
 *     <dialog class="popup__content look-bundle-popup__dialog" data-popup-dialog>
 *       ...
 *       <h2 data-popup-title></h2>
 *       <a data-popup-view-link>...</a>
 *       <div data-popup-body></div>
 *       <button class="popup__close">...</button>
 *     </dialog>
 *   </look-bundle-popup>
 */

if (!customElements.get('look-bundle-popup')) {
  class LookBundlePopup extends HTMLElement {
    constructor() {
      super();
      this._onTriggerClick = this._onTriggerClick.bind(this);
      this._onDialogClick = this._onDialogClick.bind(this);
      this._onClose = this._onClose.bind(this);
      this._renderCache = new Map();
    }

    connectedCallback() {
      // Dedup: if another instance is already mounted, hide and disable this one.
      const existing = document.querySelector('look-bundle-popup[data-mounted]');
      if (existing && existing !== this) {
        this.remove();
        return;
      }
      this.setAttribute('data-mounted', '');

      this.dialog = this.querySelector('[data-popup-dialog]');
      this.titleEl = this.querySelector('[data-popup-title]');
      this.bodyEl = this.querySelector('[data-popup-body]');
      this.viewLinkEl = this.querySelector('[data-popup-view-link]');
      this.footerEl = this.querySelector('[data-popup-footer]');
      this.closeBtn = this.querySelector('.popup__close');

      if (!this.dialog) return;

      document.addEventListener('click', this._onTriggerClick);
      this.dialog.addEventListener('click', this._onDialogClick);
      this.dialog.addEventListener('close', this._onClose);
      if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());

      // Reveal the host (was [hidden] for the no-JS path).
      this.removeAttribute('hidden');
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onTriggerClick);
      if (this.dialog) {
        this.dialog.removeEventListener('click', this._onDialogClick);
        this.dialog.removeEventListener('close', this._onClose);
      }
    }

    _onTriggerClick(event) {
      const trigger = event.target.closest('[data-look-bundle-trigger]');
      if (!trigger) return;
      // The trigger is a sibling of the card link, so there's no native nav to
      // prevent — but we still call preventDefault in case it ends up inside
      // an anchor in a future refactor.
      event.preventDefault();
      event.stopPropagation();
      this.openFromTrigger(trigger);
    }

    openFromTrigger(trigger) {
      const handle = trigger.dataset.lookHandle || '';
      const title = trigger.dataset.lookTitle || '';
      const url = trigger.dataset.lookUrl || '';

      if (this.titleEl) this.titleEl.textContent = title;
      if (this.viewLinkEl) {
        if (url) {
          this.viewLinkEl.setAttribute('href', url);
          this.viewLinkEl.hidden = false;
          if (this.footerEl) this.footerEl.hidden = false;
        } else {
          this.viewLinkEl.hidden = true;
          if (this.footerEl) this.footerEl.hidden = true;
        }
      }

      // Open immediately for a snappy feel; the body fills in once loaded.
      this.open();
      this._loadBundle(handle, url, trigger);
    }

    /**
     * Populate the drawer body with a look's product cards.
     * Uses the per-handle HTML cache when available, otherwise fetches the
     * cards from `<url>?section_id=look-bundle-content` (Section Rendering API).
     */
    _loadBundle(handle, url, trigger) {
      if (!this.bodyEl) return;

      // Guard against out-of-order responses when a shopper opens one look and
      // quickly opens another: only the newest request may paint.
      const reqId = (this._reqId = (this._reqId || 0) + 1);
      const isStale = () => reqId !== this._reqId;

      const cached = handle && this._renderCache.get(handle);
      if (cached != null) {
        this.bodyEl.innerHTML = cached;
        window.resetKlaviyoBIS?.();
        return;
      }

      if (!url) {
        this._renderError();
        return;
      }

      this.bodyEl.innerHTML = this._loadingMarkup();
      if (trigger) trigger.setAttribute('aria-busy', 'true');

      const endpoint = url + (url.indexOf('?') === -1 ? '?' : '&') + 'section_id=look-bundle-content';

      fetch(endpoint, { credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.text();
        })
        .then((html) => {
          if (isStale()) return;
          const markup = this._extractBundle(html);
          if (handle) this._renderCache.set(handle, markup);
          this.bodyEl.innerHTML = markup;
          window.resetKlaviyoBIS?.();
        })
        .catch(() => {
          if (isStale()) return;
          this._renderError();
        })
        .finally(() => {
          if (trigger) trigger.removeAttribute('aria-busy');
        });
    }

    /**
     * Section Rendering returns the cards wrapped in the section's
     * `<div id="shopify-section-look-bundle-content" class="shopify-section">`.
     * Unwrap it so the `.look-bundle-popup__item` cards are direct children of
     * the flex body (otherwise the column gap / layout won't apply).
     */
    _extractBundle(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const section = doc.querySelector('.shopify-section');
      return (section ? section.innerHTML : doc.body.innerHTML).trim();
    }

    _loadingMarkup() {
      return (
        '<div class="look-bundle-popup__loading" role="status" aria-live="polite">' +
        '<span class="look-bundle-popup__spinner" aria-hidden="true"></span>' +
        '<span class="visually-hidden">' +
        (window.themeStrings && window.themeStrings.loading ? window.themeStrings.loading : 'Loading') +
        '</span></div>'
      );
    }

    _renderError() {
      if (!this.bodyEl) return;
      this.bodyEl.innerHTML =
        '<p class="look-bundle-popup__error" role="alert">' +
        (window.themeStrings && window.themeStrings.bundleError
          ? window.themeStrings.bundleError
          : "We couldn't load this look right now. Please try again or view the full look below.") +
        '</p>';
    }

    open() {
      if (!this.dialog) return;
      if (typeof this.dialog.showModal === 'function' && !this.dialog.open) {
        this.dialog.showModal();
      } else if (!this.dialog.open) {
        // Older browsers: fall back to attribute toggle + classes.
        this.dialog.setAttribute('open', '');
      }
      document.body.classList.add('modal-open', 'look-bundle-popup-open');
      this.dispatchEvent(new CustomEvent('look-bundle:opened', { bubbles: true }));
    }

    close() {
      if (!this.dialog) return;
      if (typeof this.dialog.close === 'function' && this.dialog.open) {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
        this._onClose();
      }
    }

    _onDialogClick(event) {
      // Klaviyo's BIS trigger is appended into the cards *after* resetKlaviyoBIS,
      // so we can't bind to it directly. Delegate instead: when its button is
      // clicked we only close our drawer so the (lower) Klaviyo modal isn't
      // hidden under our <dialog> top layer. We must NOT stop propagation —
      // Klaviyo's own listener lives on <body> and needs this same click to
      // bubble up and open the Back-in-Stock modal.
      if (event.target.closest('.klaviyo-bis-trigger')) {
        // Defer so Klaviyo's <body> listener handles this same click (and reads
        // the still-attached trigger) before _onClose() empties the drawer body.
        requestAnimationFrame(() => this.close());
        return;
      }

      // Click on the dialog (backdrop) — close. Inner content stops propagation
      // via the .look-bundle-popup__inner element below.
      if (event.target === this.dialog) {
        this.close();
      }
    }

    _onClose() {
      document.body.classList.remove('modal-open', 'look-bundle-popup-open');
      if (this.bodyEl) this.bodyEl.replaceChildren();
      this.dispatchEvent(new CustomEvent('look-bundle:closed', { bubbles: true }));
    }
  }

  customElements.define('look-bundle-popup', LookBundlePopup);
}
